// Handlers for screen capture

import { atom, clean } from "nanostores";
import { acquireAudioTrack, client, localAudioTrack } from "./rtc-handlers";
import { Dispatcher } from "./actions";
import { Err } from "space-lift";

const getAudioContext = () => typeof window !== undefined && window.AudioContext || (window as any).webkitAudioContext;

interface RecorderState {
    screenShareStream: MediaStream | undefined;
    screenshareAudioSourceNode: MediaStreamAudioSourceNode | undefined;
    audioContext: AudioContext | undefined;
    micAudioSourceNode: MediaStreamAudioSourceNode | undefined;
    audioDestination: MediaStreamAudioDestinationNode | undefined;
    currentState: 'IDLE' | 'RECORDING' | 'PAUSED' | 'STOPPED'
    startTime: number | undefined;
    recorder: MediaRecorder | undefined;
    finalBlob: string;
}

export const recorderState = atom<RecorderState>({
    screenShareStream: undefined,
    audioDestination: undefined,
    audioContext: undefined,
    currentState: 'IDLE',
    startTime: undefined,
    recorder: undefined,
    finalBlob: '',
    micAudioSourceNode: undefined,
    screenshareAudioSourceNode: undefined,
});

let mediaChunks: Blob[] = [];
const MEDIA_RECORDER_TIMESLICE_MS = 2000;


export const checkSupported = async () => {
    if (!navigator.mediaDevices) {
        throw new Error('MediaDevices not available in your browser. You may be able to enable this in Experimental Features');
    }
    if (typeof MediaRecorder === 'undefined') {
        throw new Error('MediaRecorder not available in your browser. You may be able to enable this in Experimental Features');
    }
}

export const getScreenShareStream = async () => {
    const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
        preferCurrentTab: true,
    } as any);

    recorderState.set({
        ...recorderState.get(),
        screenShareStream: stream,
    });

    return stream;
}

export const cleanup = () => {
    const state = recorderState.get();
    if (state.recorder?.state !== 'inactive') {
        state.recorder?.stop();
    }

    if (state.micAudioSourceNode) {
        state.micAudioSourceNode.disconnect();
    }

    // If we're not recording, and there is no mic producer, then we can disable the local audio track
    if (!client.micProducer) {
        if (localAudioTrack) {
            localAudioTrack.enabled = false;
        }
    }

    if (state.screenshareAudioSourceNode) {
        state.screenshareAudioSourceNode.disconnect();
    }

    state.screenShareStream?.getTracks().forEach(track => track.stop());

    mediaChunks = [];
    recorderState.set({
        ...state,
        screenShareStream: undefined,
        startTime: undefined,
        recorder: undefined,
        audioDestination: undefined,
        micAudioSourceNode: undefined,
        screenshareAudioSourceNode: undefined,
        finalBlob: state.currentState === 'IDLE' ? '' : state.finalBlob,
    });
}

export const startMic = async () => {
    const state = recorderState.get();
    if (state.currentState !== 'RECORDING' && state.currentState !== 'PAUSED') {
        throw new Error('Not recording right now');
    }

    if (!state.audioDestination || !state.audioContext) {
        throw new Error("Audio context not available")
    }

    let micAudioStreamSource = state.micAudioSourceNode

    if (!micAudioStreamSource) {
        if (!localAudioTrack) {
            await acquireAudioTrack();
        }


        const audioStream = new MediaStream();
        audioStream.addTrack(localAudioTrack!);
        micAudioStreamSource = state.audioContext.createMediaStreamSource(audioStream);
    }

    micAudioStreamSource.connect(state.audioDestination);

    recorderState.set({
        ...recorderState.get(),
        micAudioSourceNode: micAudioStreamSource
    })
};

export const disableMic = async () => {
    const state = recorderState.get();

    if (!state.micAudioSourceNode) {
        throw new Error("Mic not enabled");
    }

    state.micAudioSourceNode.disconnect();

    recorderState.set({
        ...recorderState.get(),
        micAudioSourceNode: undefined,
    });
}

export const startRecording = async (micEnabled = false) => {
    const state = recorderState.get();
    if (state.currentState == 'RECORDING') {
        return;
    }

    const screenShareStream = state.screenShareStream;
    if (!screenShareStream) {
        throw new Error('Screen share stream not available');
    }

    const startTime = Date.now().valueOf();

    const preferredOptions = { mimeType: 'video/mp4;codecs=avc1' };
    const backupOptions = { mimeType: 'video/webm;codecs=vp9' };
    const lastResortOptions = { mimeType: 'video/webm;codecs=vp8,opus' };
    let options = preferredOptions;

    if (typeof MediaRecorder.isTypeSupported === 'function') {
        if (!MediaRecorder.isTypeSupported(preferredOptions.mimeType)) {
            options = backupOptions;
            if (!MediaRecorder.isTypeSupported(options.mimeType)) {
                options = lastResortOptions;
            }
        }
    }

    mediaChunks = [];

    let micAudioStreamSource: MediaStreamAudioSourceNode | undefined;
    let screenshareAudioStreamSource: MediaStreamAudioSourceNode | undefined;

    const AudioContext = getAudioContext();

    const audioContext = new AudioContext() as AudioContext;
    const audioDestination = audioContext.createMediaStreamDestination();


    const screenshareAudioTrack = screenShareStream.getAudioTracks()[0];
    if (screenshareAudioTrack) {
        const audioStream = new MediaStream();
        audioStream.addTrack(screenshareAudioTrack);
        screenshareAudioStreamSource = audioContext.createMediaStreamSource(audioStream);
        screenshareAudioStreamSource.connect(audioDestination);
    }

    if (micEnabled) {
        if (!localAudioTrack) {
            await acquireAudioTrack();
        }
        const audioStream = new MediaStream();
        audioStream.addTrack(localAudioTrack!);
        micAudioStreamSource = audioContext.createMediaStreamSource(audioStream);
        micAudioStreamSource.connect(audioDestination);
    }

    const recorder = new MediaRecorder(new MediaStream([
        screenShareStream.getVideoTracks()[0],
        audioDestination.stream.getAudioTracks()[0],
    ]), options);

    recorder.start(MEDIA_RECORDER_TIMESLICE_MS);

    recorder.ondataavailable = (event) => {
        mediaChunks.push(event.data);
    }

    recorder.onstop = async () => {
        const blob = new Blob(mediaChunks, { type: recorder.mimeType });
        recorderState.set({
            ...recorderState.get(),
            finalBlob: await URL.createObjectURL(blob),
        });
        cleanup();
    }

    recorderState.set({
        ...state,
        currentState: 'RECORDING',
        startTime,
        recorder,
        micAudioSourceNode: micAudioStreamSource,
        screenshareAudioSourceNode: screenshareAudioStreamSource,
        audioDestination,
        audioContext,
    });
}


export const pauseRecording = async () => {
    const state = recorderState.get();

    if (state.currentState !== 'RECORDING' || !state.recorder) {
        throw Error("Not recording right now!");
    }


    state.recorder.pause();

    recorderState.set({
        ...state,
        currentState: 'PAUSED',
    });
}

export const resumeRecording = async () => {
    const state = recorderState.get();

    if (state.currentState !== 'PAUSED' || !state.recorder) {
        throw Error("Not paused right now!");
    }

    state.recorder.resume();

    recorderState.set({
        ...state,
        currentState: 'RECORDING',
    });
}

export const stopRecording = async () => {
    const state = recorderState.get();

    if (!['PAUSED', 'RECORDING'].includes(state.currentState) || !state.recorder) {
        throw Error("Not recording right now!");
    }

    state.recorder.stop();

    recorderState.set({
        ...state,
        currentState: 'STOPPED',
    });
    // should automatically call on stop handler
}

export const resetRecorder = async () => {
    const state = recorderState.get();
    recorderState.set({
        ...state,
        currentState: 'IDLE',
    })
    cleanup();
};
